import {
  SignatureRequestStatusCode,
  SignatureStatusCode,
  SignatureTypeCode,
  type SignatureValidationKind,
  type PadesSignatureVerificationDto,
  type SignatureValidationResultDto,
  type SignatureValidationSignerDto,
  type SignatureIntegrityStatus,
} from '../../services/documentSignatureService';
import {
  normalizeSignatureRequestStatus,
  normalizeSignatureStatus,
  normalizeSignatureType,
} from '../../utils/signatureStatusUtils';

export function statusLabel(status: number | string): string {
  const normalized = normalizeSignatureRequestStatus(status);
  if (normalized === SignatureRequestStatusCode.Completed) {
    return 'Concluída';
  }
  if (normalized === SignatureRequestStatusCode.InProgress) {
    return 'Em andamento';
  }
  if (normalized === SignatureRequestStatusCode.Cancelled) {
    return 'Cancelada';
  }
  return 'Pendente';
}

export function signerStatusLabel(status: number | string): string {
  const normalized = normalizeSignatureStatus(status);
  if (normalized === SignatureStatusCode.Signed) {
    return 'Assinado';
  }
  if (normalized === SignatureStatusCode.Rejected) {
    return 'Recusado';
  }
  return 'Pendente';
}

export function resolveSignatureKind(signer: SignatureValidationSignerDto): SignatureValidationKind {
  if (signer.signatureKind) {
    return signer.signatureKind;
  }

  const signatureType = normalizeSignatureType(signer.signatureType);
  if (signatureType === SignatureTypeCode.Certificate) {
    return 'icp_brasil';
  }

  if (signatureType === SignatureTypeCode.Handwritten) {
    return 'handwritten';
  }

  return 'electronic';
}

export function signatureKindBadge(
  signatureKind: SignatureValidationKind,
  barukSealApplied: boolean
): { label: string; className: string } {
  if (signatureKind === 'icp_brasil') {
    return {
      label: 'Assinatura ICP-Brasil',
      className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    };
  }

  if (signatureKind === 'handwritten') {
    return {
      label: barukSealApplied
        ? 'Assinatura manuscrita — coberta por selo PAdES Baruk'
        : 'Assinatura manuscrita',
      className: 'bg-sky-50 text-sky-900 border-sky-200',
    };
  }

  return {
    label: barukSealApplied
      ? 'Assinatura eletrônica — coberta por selo PAdES Baruk'
      : 'Assinatura eletrônica',
    className: 'bg-sky-50 text-sky-900 border-sky-200',
  };
}

export function isSha256Hash(value: string): boolean {
  return /^[a-f0-9]{64}$/i.test(value.trim());
}

/** Detecta IP (IPv4, IPv6 ou IPv4 mapeado) — não é ID público de validação. */
export function isLikelyIpAddress(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (/^::ffff:\d{1,3}(\.\d{1,3}){3}$/i.test(trimmed)) {
    return true;
  }

  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(trimmed)) {
    return true;
  }

  if (trimmed.includes(':') && /^[0-9a-f:]+$/i.test(trimmed)) {
    return true;
  }

  return false;
}

export function integrityBadge(
  status: SignatureIntegrityStatus | null | undefined
): { label: string; className: string } | null {
  if (!status) {
    return null;
  }

  if (status === 'match_final') {
    return {
      label: 'Integridade: o arquivo confere com o documento final registrado no Know InDoc',
      className: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    };
  }

  if (status === 'match_original') {
    return {
      label: 'Integridade: o arquivo confere com o PDF original registrado no Know InDoc',
      className: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    };
  }

  if (status === 'mismatch') {
    return {
      label: 'Integridade: o hash do arquivo não confere com o registro Know InDoc',
      className: 'bg-amber-50 text-amber-900 border-amber-200',
    };
  }

  if (status === 'unknown') {
    return {
      label:
        'Nenhum registro encontrado no Know InDoc para este arquivo. O PDF pode ser válido, mas não consta no sistema.',
      className: 'bg-slate-100 text-slate-700 border-slate-200',
    };
  }

  return null;
}

export function hasRegistryMetadata(result: SignatureValidationResultDto): boolean {
  return Boolean(result.publicId && result.documentTitle);
}

export function hasEmbeddedSignatures(result: SignatureValidationResultDto): boolean {
  return Boolean(result.embeddedSignatures && result.embeddedSignatures.length > 0);
}

/** Upload do PDF original (pré-assinatura): bate no registro, mas o arquivo não contém PAdES. */
export function isUploadedOriginalWithoutEmbeddedSignatures(
  result: SignatureValidationResultDto
): boolean {
  return Boolean(
    result.uploadedSha256 &&
      result.integrityStatus === 'match_original' &&
      !hasEmbeddedSignatures(result)
  );
}

/**
 * Selo Baruk, badges de tipo de assinatura e mensagens de “documento assinado” aplicam-se
 * ao artefato verificado (PDF final ou com PAdES embutido), não ao original enviado.
 */
export function showSigningClaimsForUploadedFile(result: SignatureValidationResultDto): boolean {
  if (!hasRegistryMetadata(result)) {
    return false;
  }

  if (!result.uploadedSha256) {
    return true;
  }

  return !isUploadedOriginalWithoutEmbeddedSignatures(result);
}

export function isPadesCryptographicallyValid(signatures: PadesSignatureVerificationDto[]): boolean {
  return signatures.length > 0 && signatures.every((s) => s.signatureIntact);
}

export function isPadesCryptographicallyInvalid(signatures: PadesSignatureVerificationDto[]): boolean {
  return signatures.length > 0 && signatures.some((s) => !s.signatureIntact);
}

export function validationSummaryMessage(result: SignatureValidationResultDto): string | null {
  const signatures = result.embeddedSignatures ?? [];
  const padesOk = isPadesCryptographicallyValid(signatures);
  const padesBad = isPadesCryptographicallyInvalid(signatures);

  if (padesBad) {
    return 'Assinatura digital inválida ou documento alterado após assinatura.';
  }

  if (isUploadedOriginalWithoutEmbeddedSignatures(result)) {
    return 'O arquivo enviado corresponde ao documento antes da assinatura no Know InDoc. Este PDF não contém assinaturas digitais.';
  }

  if (result.integrityStatus === 'match_final' && padesOk) {
    return 'Documento íntegro e assinatura digital verificada.';
  }

  if (result.integrityStatus === 'match_final' && !hasEmbeddedSignatures(result)) {
    return 'O arquivo confere com o documento final registrado no Know InDoc.';
  }

  if (result.integrityStatus === 'mismatch' && padesOk) {
    return 'O PDF possui assinatura digital válida, mas não corresponde ao documento registrado nesta solicitação Know InDoc.';
  }

  if (result.integrityStatus === 'unknown' && padesOk) {
    return 'Assinatura digital válida no arquivo. Nenhum registro Know InDoc encontrado para este hash.';
  }

  return null;
}

export function padesIntegrityBadge(intact: boolean): { label: string; className: string } {
  if (intact) {
    return {
      label: 'Integridade da assinatura: válida',
      className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    };
  }

  return {
    label: 'Integridade da assinatura: inválida',
    className: 'bg-red-50 text-red-800 border-red-200',
  };
}

export function padesChainBadge(valid: boolean): { label: string; className: string } {
  if (valid) {
    return {
      label: 'Cadeia ICP-Brasil: válida',
      className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    };
  }

  return {
    label: 'Cadeia ICP-Brasil: não validada',
    className: 'bg-amber-50 text-amber-900 border-amber-200',
  };
}
